import { Loading, Notify } from 'quasar';
import { t } from 'boot/i18n';
import { useStrapiPost } from 'src/shared/composables/useStrapiApi';
import { useAuth } from 'src/boot/auth';

/**
 * Credential sign-in shared by the parent and school login pages.
 *
 * The two flows are deliberately separate products — each page shows only its
 * own branding, registration and links, and neither advertises the other. That
 * separation is about what users are *shown*, not about which credentials are
 * accepted: both pages POST the same `auth/local` and get back the same JWT, so
 * neither inspects the role. `signIn()` lands each account on its own home, so a
 * student who happened to arrive at the parent page simply ends up in the school
 * app. Refusing a valid password because of the page it was typed into would
 * only make them type it again — and reveal the other flow while doing it.
 */
export function useAccountSignIn() {
  const { signIn } = useAuth();

  /**
   * Authenticate and hand off to `signIn()`, which routes by role.
   * Returns false when the credentials were refused (the toast is already shown).
   */
  async function signInWithCredentials(
    identifier: string,
    password: string,
  ): Promise<boolean> {
    // suppressError: a wrong password is an ordinary outcome, not a system fault.
    // The generic handler would render it as "Bad Request (400)"; one plain
    // message reads better and avoids stacking two toasts for one failure.
    const { execute, error, data } = useStrapiPost(
      'auth/local',
      { identifier, password },
      { suppressError: true },
    );

    Loading.show();
    await execute().catch(() => null);
    Loading.hide();

    const jwt = (data.value as { jwt?: string } | null)?.jwt;
    if (error.value || !jwt) {
      Notify.create({ message: t('AUTHENTICATION_FAIL'), color: 'negative' });
      return false;
    }

    await signIn(jwt);
    return true;
  }

  return { signInWithCredentials };
}
